import {
    Checkbox,
    DefaultButton, IPanelProps,
    IRenderFunction, MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Separator, Stack, TextField, Toggle
} from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import { fadeIn, panelButtonsStyles } from "global/Application";
import { useApplication } from "global/Application/Context";
import { addUpdateAccountsReceivable } from "global/Reducer/dataSlice";
import { IPanelEvent } from "model/Extension";
import {
    IAccountsReceivable as FAM_IAccountsReceivable
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";

interface IData {
    accountsReceivable: FAM_IAccountsReceivable | null;
}

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IData>) => void;

    // start condition
    data?: IData;
}

const AccountsReceivableProperties = (props: IProps) => {

    const dispatch = useDispatch();

    const application = useApplication();

    // current view state/stages
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    // selected category
    const [accountsReceivable, setAccountsReceivable] = useState<FAM_IAccountsReceivable | null>(null);

    const [cascadeDeactivateFlag, setCascadeDeactivateFlag] = useState<boolean | null>(null);
    const [cascadeDeactivateNonstandard, setCascadeDeactivateNonstandard] = useState<boolean>(false);
    const [cascadeDeactivateAttributeSet, setCascadeDeactivateAttributeSet] = useState<boolean>(false);

    useEffect(() => {
        if (props.isOpen) {

            if (props.data?.accountsReceivable)
                setAccountsReceivable(structuredClone(props.data.accountsReceivable));

            setCascadeDeactivateFlag(null);
            setCascadeDeactivateNonstandard(false);
            setCascadeDeactivateAttributeSet(false);

        }
    }, [props.isOpen]);

    function onRenderAddProperties(): JSX.Element {

        if (!accountsReceivable)
            return <div>Accounts Receivable not available!</div>;

        return (
            <div className={fadeIn}>
                <Stack tokens={{ childrenGap: 10 }}>

                    <TextField
                        label='Name'
                        value={accountsReceivable.name ?? ''}
                        onChange={(event, value) => {
                            setAccountsReceivable(t => { return { ...t!, name: value ?? '' }; });

                        }}
                    />

                    <Toggle
                        label={"Deactivate"}
                        disabled={!application.ChangeTypePermissions.canDeactivateAccountReceivable}
                        checked={accountsReceivable.deactivated ?? false}
                        onChange={(_, checked) => {
                            setAccountsReceivable(t => { return { ...t!, deactivated: checked ?? false }; });

                            setCascadeDeactivateFlag(checked ?? false);
                            setCascadeDeactivateNonstandard(checked ?? false);
                            setCascadeDeactivateAttributeSet(checked ?? false);
                        }}
                    />

                    {cascadeDeactivateFlag != null &&
                        <CustomPanel title="Cascade Deactivation Change" >
                            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { padding: '10px' } }}>
                                <Checkbox
                                    label={`${accountsReceivable.deactivated ? "Deactivate" : "Activate"} all Nonstandards`}
                                    checked={cascadeDeactivateNonstandard}
                                    disabled={!application.ChangeTypePermissions.canDeactivateAccountReceivable || accountsReceivable.deactivated}
                                    onChange={(_, checked) => {
                                        setCascadeDeactivateNonstandard(checked ?? false);
                                    }}
                                />

                                <Checkbox
                                    label={`${accountsReceivable.deactivated ? "Deactivate" : "Activate"} all Attributesets`}
                                    checked={cascadeDeactivateAttributeSet}
                                    disabled={!application.ChangeTypePermissions.canDeactivateAccountReceivable || accountsReceivable.deactivated}
                                    onChange={(_, checked) => {
                                        setCascadeDeactivateAttributeSet(checked ?? false);
                                    }}
                                />
                            </Stack>

                        </CustomPanel>
                    }


                </Stack>
            </div>
        );
    }

    function onRenderAddPropertiesFooter(): JSX.Element | null {
        if (!accountsReceivable)
            return <div>Plan not available!</div>;

        return <div className={fadeIn}>
            <PrimaryButton text='OK'
                styles={panelButtonsStyles}
                disabled={accountsReceivable.name === ''}
                onClick={() => {

                    // TODO for nonstandard
                    // // cascade deactivation to nonstandards and attribute sets
                    // if (cascadeDeactivateNonstandard)
                    //     accountsReceivable.nonStandards.forEach(eachNonStandard => {
                    //         eachNonStandard.deactivated = accountsReceivable.deactivated;
                    //     });

                    if (cascadeDeactivateAttributeSet)
                        accountsReceivable.attributeSets.forEach(eachAttributeSet => {
                            eachAttributeSet.deactivated = accountsReceivable.deactivated;
                        });

                    dispatch(addUpdateAccountsReceivable([accountsReceivable]));
                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={props.onDismiss}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    const onRenderUnknown: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return (<div>Unknown Status</div>)
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        onRender = onRenderAddProperties;

        return (
            <div>
                <Separator />
                {validationMessage &&
                    <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                        {validationMessage}
                    </MessageBar>
                }

                {onRender()}
            </div>
        );
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;
        onRender = onRenderAddPropertiesFooter;

        return onRender();
    }

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="Properties"
            // onRenderHeader={onRenderHeader}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(AccountsReceivableProperties);
